import { createAPI } from './shared/camapi';
import flvjs from 'flv.js';

declare global {
  interface Window {
    proxy: string,
    stream: string,
    cam_id: number,
    autoclose: number,
  }
}

async function main() {
  const video = document.querySelector<HTMLVideoElement>('video#video')!;
  const loading = document.querySelector('.stream-wrapper .loading')!;
  const control = document.querySelector('.ptz-control')!;
  const channel = 0;
  let speed = 10;
  let zoomInterval: number | undefined = undefined;

  const { ptzAbility, ptzControl, ptzToPos, ptzZoomFocus, ptzGetPresets, ptzGetZoomFocus, logout } = await createAPI(channel);
  
  let player: flvjs.Player | undefined = undefined;
  const play = () => {
    if (player !== undefined) return;
    if (!flvjs.isSupported()) {
      loading.classList.add('error');
      loading.textContent = 'FLV.js is not supported in this browser!';
    } else {
      try {
        console.log('Resuming player');
        player = flvjs.createPlayer({
          type: 'flv',
          isLive: true,
          url: window.stream,
        }, {
          enableWorker: false,
          enableStashBuffer: false,
          autoCleanupSourceBuffer: true,
        });
        player.attachMediaElement(video);
        player.on(flvjs.Events.ERROR, (...args) => {
          console.error('FLV.js error', ...args);
          loading.classList.remove('hidden');
          loading.classList.add('error');
          loading.textContent = args.join(' ');
        });
        player.on(flvjs.Events.MEDIA_INFO, async () => {
          try {
            console.debug("Got MEDIA_INFO");
            await video.play();
            video.muted = false;
            await video.play();
            document.getElementById('click-msg')?.remove();
          } catch (e) {
            console.error(e);
          }
        });
        player.load();
        console.log('Loaded player');
        player.play();
        loading.classList.add('hidden');
        video.addEventListener('canplaythrough', jumpToEndBuffer, { once: true });
        video.muted = false;
        console.log('Player done');
      } catch (e) {
        console.error(e);
      }
    }
  };
  const pause = () => {
    console.log('Pausing player');
    player?.destroy();
    player = undefined;
    loading.classList.remove('hidden', 'error');
    loading.textContent = 'Pausiert';
  };
  const jumpToEndBuffer = () => {
    if (player === undefined || video.paused) return;
    console.debug('Jumping to end of buffer to reduce lag');
    // inspired by reolinks logic, but they do some more stuff (to smooth it out)
    const buf = video.buffered;
    if (!buf.length) return;
    const end = video.buffered.end(buf.length - 1);
    if (end - video.currentTime < 1) return;
    video.currentTime = end - 0.1;
  };
  setInterval(jumpToEndBuffer, 5000);
  document.addEventListener('click', async () => {
    try {
      video.muted = false;
      await video.play();
      document.getElementById('click-msg')?.remove();
    } catch (e) {
      console.error(e);
    }
  }, { once: true });
  video.addEventListener('play', async () => {
    try {
      video.muted = false;
      if (video.paused) return;
      document.getElementById('click-msg')?.remove();
    } catch (e) {
      console.error(e);
    }
  });

  let moveInterval = setTimeout(pause, window.autoclose * 1000);
  document.addEventListener('mousemove', () => {
    if (player === undefined) play();
    clearTimeout(moveInterval);
    moveInterval = setTimeout(pause, window.autoclose * 1000);
  });
  
  try {
    function setVideoFill() {
      video!.classList.toggle('contain', videoFill.value === 'contain');
      window.localStorage.setItem('videoFill', videoFill.value);
    }
    const videoFill = control.appendChild(document.createElement('select'));
    videoFill.options.add(document.createElement('option'));
    videoFill.options.add(document.createElement('option'));
    videoFill.options[0].value = 'cover';
    videoFill.options[0].textContent = 'Füllend';
    videoFill.options[1].value = 'contain';
    videoFill.options[1].textContent = 'Voll';
    videoFill.addEventListener('change', setVideoFill);
    videoFill.value = window.localStorage.getItem('videoFill') || 'cover';
    setVideoFill();

    function constructPlusMinus(name: string) {
      const c = control.appendChild(document.createElement('div'));
      c.className = 'ptz-field';
      const label = c.appendChild(document.createElement('label'));
      label.textContent = name + ':';
      const val = c.appendChild(document.createElement('input'));
      val.className = 'value';
      val.type = 'number';
      const minus = c.appendChild(document.createElement('button'));
      minus.textContent = '-';
      const plus = c.appendChild(document.createElement('button'));
      plus.textContent = '+';
      return { val, minus, plus };
    }
    
    const val = await ptzAbility();
    const ver = val.Ability.abilityChn[channel].ptzType.ver;
    console.log('ptzType', ver);
    if (ver > 1) {
      const wrapper = document.createElement('div');
      wrapper.className = 'ptz-pad';
      const btns = Array.from({ length: 9 }, _ => document.createElement('button'));
      wrapper.append(...btns);
      const actions = ['LeftUp', 'Up', 'RightUp', 'Left', 'Stop', 'Right', 'LeftDown', 'Down', 'RightDown'];
      const text = ['LU', 'U', 'RU', 'L', '', 'R', 'LD', 'D', 'RD'];
      for (let i = 0; i < btns.length; ++i) {
        const btn = btns[i];
        const action = actions[i];
        btn.textContent = text[i];
        btn.addEventListener('click', () => {
          const p = ptzControl(action, {speed});
          if (action != 'Stop') p.then(() => ptzControl('Stop'));
        });
      }
      control.append(wrapper);

      const speedC = constructPlusMinus('Speed');
      speedC.plus.addEventListener('click', () => {speed += 1; speedC.val.value = '' + speed});
      speedC.minus.addEventListener('click', () => {speed = Math.max(speed - 1, 1); speedC.val.value = '' + speed});
      speedC.val.addEventListener('change', () => {speed = Math.max(+speedC.val.value, 1); speedC.val.value = '' + speed});
      speedC.val.value = '' + speed;
    }

    const cver = val.Ability.abilityChn[channel].ptzCtrl.ver;
    console.log('ptzCtrl', cver);
    if (cver >= 1) {
      const zoom = constructPlusMinus('Zoom');
      const focus = constructPlusMinus('Focus');

      async function updateZoomFocus() {
        const val = await ptzGetZoomFocus();
        zoom.val.value = '' + val.ZoomFocus.zoom.pos;
        focus.val.value = '' + val.ZoomFocus.focus.pos;
      }
      zoom.plus.addEventListener('click', () => ptzControl('ZoomStepInc').then(updateZoomFocus));
      zoom.minus.addEventListener('click', () => ptzControl('ZoomStepDec').then(updateZoomFocus));
      zoom.val.addEventListener('change', () => ptzZoomFocus('ZoomPos', zoom.val.value).then(updateZoomFocus));
      focus.plus.addEventListener('click', () => ptzControl('FocusStepInc').then(updateZoomFocus));
      focus.minus.addEventListener('click', () => ptzControl('FocusStepDec').then(updateZoomFocus));
      focus.val.addEventListener('change', () => ptzZoomFocus('FocusPos', focus.val.value).then(updateZoomFocus));
      updateZoomFocus();
      zoomInterval = setInterval(updateZoomFocus, 5_000);
    }

    const pver = val.Ability.abilityChn[channel].ptzPreset.ver;
    console.log('ptzPreset', pver);
    if (pver >= 1) {
      const wrapper = document.createElement('div');
      wrapper.className = 'ptz-presets';
      const list = wrapper.appendChild(document.createElement('div'));
      
      const val = await ptzGetPresets();
      list.append(...val.PtzPreset.filter(p => p.enable).map(p => {
        const l = document.createElement('label');
        const e = l.appendChild(document.createElement('input'));
        e.type = 'radio';
        e.name = 'preset';
        e.value = '' + p.id;
        l.append(`${p.name} (${p.id})`);
        return l;
      }));
      control.append(wrapper);

      const call = wrapper.appendChild(document.createElement('button'));
      call.textContent = 'Position setzen';
      call.addEventListener('click', () => {
        const p = list.querySelector<HTMLInputElement>('input[name="preset"]:checked');
        if (!p) return;
        ptzToPos(Number(p.value));
      });
    }
  } catch (e) {
    console.error(e);
    alert((e as Error).message || e);
  }

  const close = control.appendChild(document.createElement('button'));
  close.textContent = 'Schließen';
  close.addEventListener('click', async () => {
    close.remove();
    clearInterval(zoomInterval);
    try {
      player?.destroy();
      player = undefined;
    } catch (e) {
      console.error(e);
    }
    await logout();
    setTimeout(() => window.close(), 1);
  });
}
window.addEventListener('load', main);
